import {
	Box,
	ButtonBase,
	Grid,
	List,
	ListItem,
	ListItemText,
	Typography,
	alpha,
	useTheme,
} from '@mui/material';
import { useState } from 'react';
import styles from './FipDiscoveredAccountsSection.styles';
import AddNumber from '../AddNumber';
import NotFoundHeading from './NotFoundHeading';
import FipdNotDiscovered from './FipdNotDiscovered';
import sizeFontCustom from './fontSize.styles';
import { useTranslation } from '../../hooks/useTranslation';
import { isSequentialLinkingEnabled } from '../../utils/entityConfig.utils';
import { keyboardFocusVisibleSx } from '../../utils/a11y.utils';

function FipNotDiscoveredAccountsSection(props) {
	const { t } = useTranslation();
	const theme = useTheme();
	const fipIds = props?.accountState?.noDiscoveredAccounts?.fips;
	const phoneNumber = props?.accountState?.noDiscoveredAccounts?.phoneNumber;

	// Check for dark theme
	const isDark =
		props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const isNewDiscoveryExperience = isSequentialLinkingEnabled(
		props.customDetails?.sdkOptions,
	);
	const [showChangeNumber, setShowChangeNumber] = useState(false);
	const [expandedExplanation, setExpandedExplanation] = useState(false);
	const handleChangeNumber = () => {
		props.setChangingMobileNumber(true);
		setShowChangeNumber(true);
	};
	const toggleAcordion = () => {
		setExpandedExplanation((prev) => !prev);
	};

	return (
		<Grid
			item
			xs={12}
			{...(!isNewDiscoveryExperience && { sm: 8, md: 8, lg: 5.5 })}
		>
			<List
				sx={{
					background: isDark ? '#2D2D2D' : theme.palette.primary.white,
					color: isDark ? '#FFFFFF' : '#000000',
					justifyContent: 'start',
					alignItems: 'start',
					borderRadius: '1rem',
					padding: isNewDiscoveryExperience ? '24px 16px' : '0.5rem',
					border: isNewDiscoveryExperience
						? `1px solid ${isDark ? '#3D3D3D' : '#D9D9D9'}`
						: 'none',
					...(isNewDiscoveryExperience && { width: '100%' }),
				}}
			>
				<NotFoundHeading />
				{!showChangeNumber && (
					<Box>
						<ListItem sx={{ pt: '0.125rem' }}>
							<ListItemText
								primary={
									<Box ml={'1rem'}>
										<Typography
											sx={sizeFontCustom.subHeading}
											color={isDark ? '#7A8185' : theme.palette.black60}
										>
											{t('accountDiscovery.messages.couldNotFindAccounts')}
											<span
												style={{
													color: isDark ? '#FFFFFF' : theme.palette.info.main,
													fontWeight: 700,
												}}
											>
												{' ' + phoneNumber + '. '}
											</span>
										</Typography>
										{global.sdkOptions?.allowDifferentMobile === 'Y' && (
											<ButtonBase
												disabled={props.changingMobileNumber}
												onClick={handleChangeNumber}
												sx={{
													color: isDark
														? '#FFFFFF'
														: theme.palette.primary.main,
													cursor: 'pointer',
													textDecoration: 'none',
													fontWeight: '700',
													textTransform: 'capitalize',
													...sizeFontCustom.subHeading,
													mt: '.5rem',
													borderRadius: '4px',
													p: '0.25rem',
													...keyboardFocusVisibleSx,
													'&:hover': {
														color: isDark
															? '#7A8185'
															: theme.palette.primary.main,
													},
												}}
											>
												{t('common.buttons.changeMobileNumber')}
											</ButtonBase>
										)}
									</Box>
								}
								secondary={
									<>
										<Grid
											container
											sx={{
												...styles.fipNotFoundContainer,
												'&::-webkit-scrollbar-thumb': {
													backgroundColor: alpha(
														theme.palette.primary.main,
														0.5,
													),
													borderRadius: '10px',
												},
											}}
										>
											{fipIds.map((fipId) => (
												<FipdNotDiscovered key={fipId} fipId={fipId} />
											))}
										</Grid>
										<Box>
											<ButtonBase
												onClick={toggleAcordion}
												sx={{
													cursor: 'pointer',
													...sizeFontCustom.subHeading,
													mt: '0.8rem',
													color: isDark ? '#FFFFFF' : 'primary.main',
													display: !expandedExplanation
														? 'inline-flex'
														: 'none',
													textAlign: 'left',
													borderRadius: '4px',
													p: '0.25rem',
													...keyboardFocusVisibleSx,
												}}
											>
												{t('accountDiscovery.errors.moreInfo')}
											</ButtonBase>
											{expandedExplanation && (
												<>
													<Typography
														component="div"
														py={'0.5rem'}
														sx={{
															...sizeFontCustom.subHeading,
															color: isDark ? '#FFFFFF' : 'inherit',
														}}
													>
														{t('accountDiscovery.errors.dueToEither')}
														<ul
															style={{
																...styles.list,
																color: isDark ? '#FFFFFF' : 'inherit',
															}}
														>
															<li>
																{t(
																	'accountDiscovery.errors.technicalDifficulties',
																)}
															</li>
															<li>
																{t('accountDiscovery.errors.jointAccount')}
															</li>
														</ul>
													</Typography>
													<ButtonBase
														onClick={toggleAcordion}
														sx={{
															cursor: 'pointer',
															...sizeFontCustom.subHeading,
															color: isDark ? '#FFFFFF' : 'primary.main',
															display: 'inline-flex',
															borderRadius: '4px',
															p: '0.25rem',
															...keyboardFocusVisibleSx,
														}}
													>
														{t('common.buttons.showLess')}
													</ButtonBase>
												</>
											)}
											<Typography
												sx={{
													mb: '0.8rem',
													...sizeFontCustom.subHeading,
													color: isDark ? '#FFFFFF' : 'inherit',
												}}
											>
												{t('accountDiscovery.errors.tryDiscovering')}
											</Typography>
										</Box>
									</>
								}
							/>
						</ListItem>
					</Box>
				)}
				{global.sdkOptions?.allowDifferentMobile === 'Y' &&
					showChangeNumber && (
						<AddNumber
							{...props}
							fipSelect={fipIds}
							setShowChangeNumber={setShowChangeNumber}
							isComponent={true}
						/>
					)}
			</List>
		</Grid>
	);
}

export default FipNotDiscoveredAccountsSection;
